
export interface ScaledStyle<T extends SimpleStyleValue> {
  property: string;
  getStyleValue(feature:any): T;

  getValues(n?:number):T[];
  getLabels(n?:number):string[];
}

export class CategoricalStyle<T extends SimpleStyleValue> implements ScaledStyle<T> {
  values:{[key:string]:T} = {}

  constructor(public property: string, public categories: string[], public vals: T[],public nullStyle?: T) {
    for(let i=0; i < categories.length; i++) {
      this.values[categories[i]] = vals[i];
    }
  }
  getValues(n?: number): T[] {
    return this.vals;
  }
  getLabels(n?: number): string[] {
    return this.categories;
  }

  getStyleValue(feature:any): T {
    const propVal = feature.properties[this.property];
    if((propVal===null)||(propVal===undefined)||!this.values[propVal]){
      return this.nullStyle;
    }
    return this.values[propVal];
  }
}

export class RangeStyle<T extends SimpleStyleValue> implements ScaledStyle<T> {
  labels:string[];

  constructor(public property: string, public values: T[], public breakpoints: number[],public nullStyle?: T){
    this.labels = generateContinuousLegendLabelsBreakpoints(this.breakpoints);
  }

  static forValues<T extends SimpleStyleValue>(property:string,values:T[],propertyValues:number[],nullStyle?:T):RangeStyle<T>{
    let sortedValues:number[] = propertyValues.slice().sort((a,b)=>a-b);
    // Create list of breakpoints based on range of values and number of colours
    const breakpoints:number[] = [sortedValues[0]];
    for(let i=1;i<values.length;i++){
      const idx = Math.round((i/values.length)*(sortedValues.length-1));
      breakpoints.push(sortedValues[idx]);
    }

    return new RangeStyle<T>(property,values,breakpoints,nullStyle);
  }

  static forRange<T extends SimpleStyleValue>(property:string,values:T[],min:number,max:number,nullStyle?:T):RangeStyle<T>{
    const breakpoints = generateContinuousLegendBreakpoints(min,max,values.length);
    return new RangeStyle<T>(property,values,breakpoints,nullStyle);
  }

  getValues(n?: number): T[] {
    return this.values;
  }

  getLabels(n?: number): string[] {
    return this.labels
  }

  idx(val:number): number {
    if((val===null)||(val===undefined)||(isNaN(val))){
      return -1;
    }

    for(let i=1; i < this.breakpoints.length; i++) {
      if((this.breakpoints[i]===null)||(val < this.breakpoints[i])){
        return i-1;
      }
    }
    return this.breakpoints.length - 1;
  }

  getStyleValue(feature:any): T {
    return this.getStyleForValue(feature.properties[this.property]);
  }

  getStyleForValue(v:number): T {
    const idx = this.idx(v);
    if(idx<0){
      return this.nullStyle;
    }

    return this.values[idx];
  }
}

export type SimpleStyleValue = boolean | string | number;
export type StyleValue = SimpleStyleValue | ScaledStyle<SimpleStyleValue>;


function formatLabelValue(val:number,decimalPlaces:number):string{
  if(!val){
    if(val===0){
      return '0';
    }
    return '-';
  }
  // Add thousand's separator. Source: http://stackoverflow.com/a/2901298
  var parts = val.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if(decimalPlaces===0){
    return parts[0];
  }

  if((decimalPlaces!==null) &&(decimalPlaces!==undefined) && (parts.length===2)){
    parts[1] = parts[1].substr(0,decimalPlaces);
    parts[1] = parts[1].replace(/0+$/, '');
  }
  return parts.join('.');
};

export function generateContinuousLegendBreakpoints(min:number,max:number,count:number):Array<number>{
  var result:number[] = [min];

  var delta = (max-min)/(count);
  var lower=min
  let  decimalPlaces = Math.max(0,2-(+Math.log10(max-min).toFixed()));
  decimalPlaces = Math.min(decimalPlaces,10);
  var upper;
  for(let i=1;i<(count);i++){
    upper = min+i*delta;
    result.push(upper);
    lower = upper;
  }
  return result;
}

export function generateContinuousLegendLabelsForRange(min:number,max:number,count:number):Array<string>{
  const breaks = generateContinuousLegendBreakpoints(min,max,count);
  let result = generateContinuousLegendLabelsBreakpoints(breaks);
  result.reverse();
  return result;
}

export function generateContinuousLegendLabelsBreakpoints(breaks:number[]):Array<string>{
  let min = breaks[0];
  let max = breaks[breaks.length-1];

  let lower = min;
  let upper:number;

  let  decimalPlaces = Math.max(0,2-(+Math.log10(max-min).toFixed()));
  decimalPlaces = Math.min(decimalPlaces,10);
  let extraDP = 0;

  let labelLower = formatLabelValue(lower,decimalPlaces);
  let labelUpper:string;

  const result:string[] = [];
  for(let i=1;i<breaks.length;i++){
    extraDP = 0;
    upper = breaks[i];
    labelUpper = formatLabelValue(upper,decimalPlaces);
    while(labelLower===labelUpper){
      extraDP++;
      labelUpper = formatLabelValue(upper,decimalPlaces+extraDP);
      if((decimalPlaces+extraDP)>10){
        break;
      }
    }
    result.push(`${labelLower}-${labelUpper}`);

    lower = upper;
    labelLower = labelUpper;
  }
  result.push('&ge;'+formatLabelValue(upper,decimalPlaces));
  return result;
}

